import { Box, Text, TooltipCommonProps } from '@wix/design-system';
import css from './index.module.scss';
import { FunctionComponent, useEffect, useRef } from 'react';
import { DATA_HOOKS } from '../../../../../dataHooks';
import {
  ArticleSearchResultItem,
  ExternalResourceType,
  ArticleSourceType,
  SearchResultItem,
} from '../../../../../types';
import { RenderHTML } from '../../../../RenderHTML';
import { ArticleType } from '@wix/answers-api';
import {
  Flag,
  SiteSearch,
  TaskAdd,
  Reels,
  SiteContent,
} from '@wix/wix-ui-icons-common';
import { EDITORS_NAMES, TOOLTIP_ENTER_DELAY } from '../../../../../constants';
import classNames from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ArticleMediumIcon } from '../../../../../icons/ArticleMediumIcon';
import { useOnVisible } from '../../../../../hooks/useOnVisible';

export type SearchResultDropdownItemProps = {
  searchResult: SearchResultItem;
  groupIndex: number;
  listIndex: number;
  onSearchResultClick: (
    url: string,
    type: ArticleSourceType,
    index: number,
    id?: string
  ) => Promise<void>;
  isFocused: boolean;
  itemScrolledIntoView: () => void;
};

const typeToIcon: Record<ArticleSourceType, JSX.Element> = {
  [ArticleType.ARTICLE]: <ArticleMediumIcon />,
  [ArticleType.FEATURE_REQUEST]: <TaskAdd />,
  [ArticleType.KNOWN_ISSUE]: <Flag />,
  [ArticleType.VIDEO]: <Reels />,
  [ExternalResourceType.WIX_BLOG]: <SiteContent />,
  [ExternalResourceType.WIX_LEARN]: <Reels />,
  [ExternalResourceType.WIX_SEO_LEARN]: <SiteSearch />,
};

export const SearchResultDropdownItem: FunctionComponent<
  SearchResultDropdownItemProps
> = ({
  searchResult,
  groupIndex,
  listIndex,
  onSearchResultClick,
  isFocused,
  itemScrolledIntoView,
}) => {
  const { locale } = useRouter();
  const itemRef = useRef(null);

  const isVisible = useOnVisible(itemRef);

  useEffect(() => {
    if (isVisible) {
      if (groupIndex === 1) {
        itemScrolledIntoView();
      }
    }
  }, [isVisible, searchResult]);

  const tooltipProps: TooltipCommonProps = {
    enterDelay: TOOLTIP_ENTER_DELAY,
  };

  const isInternal = Object.values(ArticleType).includes(
    searchResult.type as ArticleType
  );

  const getArticleTitle = (title: string) => {
    if (title.includes(':')) {
      const [firstPart, secondPart] = title.split(':');
      const isEditor = EDITORS_NAMES.some((editor) =>
        firstPart.includes(editor)
      );
      if (isEditor) {
        return (
          <>
            <b>{firstPart}</b>:{secondPart}
          </>
        );
      }
    }
    return title;
  };

  const customHref = isInternal
    ? `/${locale}${
        (searchResult as ArticleSearchResultItem).uri.toString().startsWith('/')
          ? ''
          : '/'
      }${(searchResult as ArticleSearchResultItem).uri}`
    : searchResult.url;

  return (
    <Link
      key={groupIndex}
      ref={itemRef}
      href={customHref}
      target={isInternal ? '_self' : '_blank'}
      onClick={async () => {
        await onSearchResultClick(
          customHref,
          searchResult.type,
          listIndex,
          searchResult?.id
        );
      }}
      data-hook={DATA_HOOKS.SEARCH_BAR_RESULT}
      data-index={listIndex}
      className={classNames(css.searchBarResult, {
        [css.focused]: isFocused,
      })}
    >
      <Box
        className={css.searchBarResultContent}
        verticalAlign="middle"
        align="space-between"
        dataHook={`${DATA_HOOKS.SEARCH_BAR_RESULT_TYPE}_${searchResult.type}`}
      >
        <Box
          className={css.dropdownResultIcon}
          dataHook={DATA_HOOKS.SEARCH_BAR_RESULT_ICON}
          direction="horizontal"
          align="center"
          verticalAlign="middle"
        >
          {typeToIcon[searchResult.type]}
        </Box>
        <Box direction="vertical" className={css.searchBarResultText}>
          <Text
            size="medium"
            weight="thin"
            ellipsis
            tooltipProps={tooltipProps}
            zIndex={10000}
            className={css.searchBarResultTitle}
            dataHook={DATA_HOOKS.SEARCH_BAR_RESULT_TITLE}
          >
            {getArticleTitle(searchResult.title)}
          </Text>
          {
            <RenderHTML
              html={searchResult.description}
              textElement={{
                className: css.searchBarResultDescription,
                weight: 'thin',
                size: 'small',
                dataHook: DATA_HOOKS.SEARCH_BAR_RESULT_DESCRIPTION,
                ellipsis: true,
                showTooltip: false,
              }}
            />
          }
        </Box>
      </Box>
    </Link>
  );
};
